import React, { useEffect, useState } from 'react'
import { RefreshControl, Alert, Platform } from 'react-native'
import { ChevronDown, ChevronRight, Users } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import {
  Button, ButtonText, EmptyState, Spinner, Text,
  XStack, YStack, ScrollView,
} from '@fliisi/pollen'
import { ScreenLayout } from '../components/ScreenLayout'

interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'MarketSellers'>
  route: RouteProp<RootStackParamList, 'MarketSellers'>
}

interface SellerWithReservations {
  id: string
  email: string
  display_name: string | null
  first_name: string | null
  last_name: string | null
  reservations: Reservation[]
  totalProducts: number
  soldProducts: number
}

interface Reservation {
  id: string
  start_date: string
  end_date: string
  status: string
  price_total: number
  space: { id: string; name: string; space_type: string } | null
  productCount: number
}

const merchantColors = { primary: '#C07A5F', primaryLight: '#D4A088', accent: '#748C70' }

export default function MarketSellersScreen({ navigation, route }: Props) {
  const { marketId } = route.params
  const { t } = useLanguage()
  const [sellers, setSellers] = useState<SellerWithReservations[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [filter, setFilter] = useState<'all' | 'active' | 'past'>('all')
  const [expandedSeller, setExpandedSeller] = useState<string | null>(null)

  useEffect(() => { fetchSellers() }, [marketId])

  const fetchSellers = async () => {
    try {
      const { data: reservations, error: resError } = await supabase
        .from('space_reservations')
        .select(`id, seller_id, start_date, end_date, status, price_total, space:market_spaces (id, name, space_type)`)
        .eq('flea_market_id', marketId)
        .order('start_date', { ascending: false })
      if (resError) throw resError
      if (!reservations || reservations.length === 0) { setSellers([]); return }

      const sellerIds = [...new Set(reservations.map((r) => r.seller_id))]
      const { data: users, error: usersError } = await supabase
        .from('users').select('id, email, display_name, first_name, last_name').in('id', sellerIds)
      if (usersError) throw usersError

      const reservationIds = reservations.map((r) => r.id)
      const { data: products, error: productsError } = await supabase
        .from('products').select('id, reservation_id, status').in('reservation_id', reservationIds)
      if (productsError) throw productsError

      const productsByReservation = new Map<string, { total: number; sold: number }>()
      products?.forEach((p) => {
        const current = productsByReservation.get(p.reservation_id) || { total: 0, sold: 0 }
        current.total++
        if (p.status === 'sold') current.sold++
        productsByReservation.set(p.reservation_id, current)
      })

      const sellerMap = new Map<string, SellerWithReservations>()
      for (const user of users || []) {
        sellerMap.set(user.id, { ...user, reservations: [], totalProducts: 0, soldProducts: 0 })
      }
      for (const res of reservations) {
        const seller = sellerMap.get(res.seller_id)
        if (seller) {
          const counts = productsByReservation.get(res.id) || { total: 0, sold: 0 }
          seller.reservations.push({
            id: res.id, start_date: res.start_date, end_date: res.end_date,
            status: res.status, price_total: res.price_total,
            space: Array.isArray(res.space) ? res.space[0] : res.space,
            productCount: counts.total,
          })
          seller.totalProducts += counts.total
          seller.soldProducts += counts.sold
        }
      }

      const sellersArray = Array.from(sellerMap.values())
      sellersArray.sort((a, b) => (b.reservations[0]?.start_date || '').localeCompare(a.reservations[0]?.start_date || ''))
      setSellers(sellersArray)
    } catch (error) {
      console.error('Error fetching sellers:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const onRefresh = () => { setRefreshing(true); fetchSellers() }

  const getSellerName = (s: SellerWithReservations) => {
    if (s.display_name) return s.display_name
    if (s.first_name || s.last_name) return `${s.first_name || ''} ${s.last_name || ''}`.trim()
    return s.email?.split('@')[0] || 'Unknown'
  }

  const getStatusColor = (status: string) => {
    const map: Record<string, string> = { confirmed: '#4CAF50', pending: '#FF9800', cancelled: '#F44336', completed: '#9E9E9E' }
    return map[status] || '#999'
  }

  const isReservationActive = (res: Reservation) => {
    const today = new Date().toISOString().split('T')[0]
    return res.start_date <= today && res.end_date >= today && res.status === 'confirmed'
  }

  const isReservationPast = (res: Reservation) => {
    const today = new Date().toISOString().split('T')[0]
    return res.end_date < today || res.status === 'completed' || res.status === 'cancelled'
  }

  const hasActiveReservation = (s: SellerWithReservations) => s.reservations.some(isReservationActive)

  const filteredSellers = sellers.filter((s) => {
    if (filter === 'active') return hasActiveReservation(s)
    if (filter === 'past') return !hasActiveReservation(s) && s.reservations.length > 0
    return true
  })

  const cancelReservation = async (reservationId: string) => {
    const message = t('confirm_cancel_reservation') || 'Cancel this reservation?'
    const doCancel = async () => {
      try {
        const { error } = await supabase.from('space_reservations').update({ status: 'cancelled' }).eq('id', reservationId)
        if (error) throw error
        setSellers((prev) => prev.map((seller) => ({
          ...seller,
          reservations: seller.reservations.map((res) => res.id === reservationId ? { ...res, status: 'cancelled' } : res),
        })))
        const successMsg = t('reservation_cancelled') || 'Reservation cancelled'
        Platform.OS === 'web' ? window.alert(successMsg) : Alert.alert(t('success') || 'Success', successMsg)
      } catch (error: any) {
        const errorMsg = error.message || t('cancel_failed') || 'Failed to cancel'
        Platform.OS === 'web' ? window.alert(errorMsg) : Alert.alert(t('error') || 'Error', errorMsg)
      }
    }
    if (Platform.OS === 'web') { if (window.confirm(message)) doCancel() }
    else Alert.alert(t('confirm') || 'Confirm', message, [
      { text: t('no') || 'No', style: 'cancel' },
      { text: t('yes') || 'Yes', style: 'destructive', onPress: doCancel },
    ])
  }

  const activeCount = sellers.filter(hasActiveReservation).length
  const pastCount = sellers.filter((s) => !hasActiveReservation(s) && s.reservations.length > 0).length

  const filterTabs = [
    { key: 'all' as const, label: `${t('all') || 'All'} (${sellers.length})` },
    { key: 'active' as const, label: `${t('active') || 'Active'} (${activeCount})` },
    { key: 'past' as const, label: `${t('past') || 'Past'} (${pastCount})` },
  ]

  return (
    <YStack f={1} bg="$background">
      {/* Header */}
      <XStack jc="space-between" ai="center" px="$md" py="$md" bg="$backgroundCard" bbw={1} bbc="$borderColor">
        <YStack w={50} />
        <Text fontSize={20} fontWeight="bold" color="$color">{t('sellers') || 'Sellers'}</Text>
        <YStack w={50} />
      </XStack>

      {/* Filter Tabs */}
      <XStack bg="$backgroundCard" px="$md" py="$sm" bbw={1} bbc="$borderColor">
        {filterTabs.map((tab) => (
          <YStack
            key={tab.key} px="$md" py="$sm" mr="$sm" br="$full" cursor="pointer"
            bg={filter === tab.key ? merchantColors.primary : '$backgroundSecondary'}
            onPress={() => setFilter(tab.key)}
            pressStyle={{ opacity: 0.8, scale: 0.98 }} animation="quick"
          >
            <Text fontSize={14} color={filter === tab.key ? '$colorInverse' : '$colorSecondary'}
              fontWeight={filter === tab.key ? '600' : '400'}>
              {tab.label}
            </Text>
          </YStack>
        ))}
      </XStack>

      {loading ? (
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
      ) : (
        <ScrollView
          f={1} contentContainerStyle={{ padding: 16, flexGrow: 1 }}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[merchantColors.primary]} tintColor={merchantColors.primary} />}
        >
          {filteredSellers.length === 0 ? (
            <YStack f={1} jc="center" ai="center" pt="$xl">
              <Text color="$colorSecondary" fontSize={16}>
                {filter === 'active' ? (t('no_active_sellers') || 'No active sellers') : (t('no_sellers') || 'No sellers with reservations yet')}
              </Text>
            </YStack>
          ) : (
            filteredSellers.map((item) => {
              const isExpanded = expandedSeller === item.id
              const hasActive = hasActiveReservation(item)
              return (
                <YStack key={item.id} bg="$backgroundCard" br="$lg" mb="$md" bw={1} bbc="$borderColor" overflow="hidden" elevation={1}>
                  <XStack
                    jc="space-between" ai="center" p="$md" cursor="pointer"
                    onPress={() => setExpandedSeller(isExpanded ? null : item.id)}
                    pressStyle={{ opacity: 0.8 }} animation="quick"
                  >
                    <YStack f={1}>
                      <XStack ai="center">
                        <Text fontSize={16} fontWeight="600" color="$color">{getSellerName(item)}</Text>
                        {hasActive && <YStack w={8} h={8} br={4} bg="#4CAF50" ml="$sm" />}
                      </XStack>
                      <Text fontSize={14} color="$colorSecondary" mt={2}>{item.email}</Text>
                      <Text fontSize={12} color="$colorSecondary" mt={4}>
                        {item.reservations.length} {t('reservations_count') || 'reservations'} • {item.totalProducts} {t('products') || 'products'} • {item.soldProducts} {t('sold') || 'sold'}
                      </Text>
                    </YStack>
                    {isExpanded ? <ChevronDown size={14} color="$colorSecondary" /> : <ChevronRight size={14} color="$colorSecondary" />}
                  </XStack>

                  {isExpanded && (
                    <YStack btw={1} btc="$borderColor" p="$md" bg="$backgroundSecondary">
                      {item.reservations.map((res) => {
                        const isPast = isReservationPast(res)
                        const isActive = isReservationActive(res)
                        return (
                          <YStack key={res.id} bg="$backgroundCard" br="$md" p="$md" mb="$sm" bw={1} bbc="$borderColor" opacity={isPast ? 0.7 : 1}>
                            <XStack jc="space-between" ai="flex-start" mb="$sm">
                              <YStack f={1}>
                                <Text fontSize={14} fontWeight="600" color="$color">{res.space?.name || 'Unknown space'}</Text>
                                <Text fontSize={12} color="$colorSecondary" textTransform="capitalize">{res.space?.space_type || ''}</Text>
                              </YStack>
                              <YStack px="$sm" py={4} br="$full" bg={getStatusColor(res.status)}>
                                <Text color="$colorInverse" fontSize={11} fontWeight="600" textTransform="capitalize">{t(res.status) || res.status}</Text>
                              </YStack>
                            </XStack>
                            <XStack jc="space-between" ai="center" mb="$sm">
                              <Text fontSize={13} color="$colorSecondary">
                                {new Date(res.start_date).toLocaleDateString()} - {new Date(res.end_date).toLocaleDateString()}
                              </Text>
                              <Text fontSize={14} fontWeight="600" color={merchantColors.primary}>{res.price_total?.toFixed(2) || '0.00'} €</Text>
                            </XStack>
                            <Text fontSize={12} color="$colorSecondary" mb="$sm">{res.productCount} {t('products') || 'products'}</Text>
                            <XStack gap="$sm">
                              <Button variant="secondary" size="sm" onPress={() => navigation.navigate('BoothView', { sellerId: item.id, reservationId: res.id })}>
                                <ButtonText>{t('view_products') || 'View Products'}</ButtonText>
                              </Button>
                              {isActive && (
                                <Button variant="outline" size="sm" onPress={() => cancelReservation(res.id)}>
                                  <ButtonText>{t('cancel') || 'Cancel'}</ButtonText>
                                </Button>
                              )}
                            </XStack>
                          </YStack>
                        )
                      })}
                    </YStack>
                  )}
                </YStack>
              )
            })
          )}
          <YStack h={80} />
        </ScrollView>
      )}
    </YStack>
  )
}
